import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CandidateHeader.css';
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  PersonOutline as PersonIcon,
  LogoutOutlined as LogoutIcon,
  SettingsOutlined as SettingsIcon,
  KeyboardArrowDown as ArrowDownIcon,
  AccountCircleOutlined as AccountIcon,
} from '@mui/icons-material';
import { showSuccess, showInfo } from '../utils/sweetAlertConfig';
import NotificationBell from './NotificationBell';

const CandidateHeader = ({ candidate }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [candidateData, setCandidateData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const open = Boolean(anchorEl);

  useEffect(() => {
    // Load candidate data from props or localStorage
    if (candidate) {
      setCandidateData(candidate);
    } else {
      const stored = localStorage.getItem('candidate_data');
      if (stored) {
        setCandidateData(JSON.parse(stored));
      }
    }
  }, [candidate]);

  useEffect(() => {
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleMenuClose();
    // Already on profile page, just show info
    showInfo(
      'Profile',
      'You are currently viewing your profile. Update your information below.'
    );
  };

  const handleSettings = () => {
    handleMenuClose();
    showInfo(
      'Settings',
      'Contact administrator for any account changes.'
    );
  };

  const handleLogout = () => {
    handleMenuClose();
    
    // Clear candidate data
    localStorage.removeItem('candidate_token');
    localStorage.removeItem('candidate_data');
    
    showSuccess('Logged Out', 'You have been successfully logged out.');
    
    // Navigate to candidate login
    navigate('/candidate/login');
  };

  const getInitials = (name) => {
    if (!name) return 'C';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getCompletionPercentage = () => {
    if (!candidateData) return 0;
    
    const fields = ['full_name', 'email', 'mobile', 'ssn', 'birth_date', 'gender'];
    const filledFields = fields.filter(field => candidateData[field] && candidateData[field].toString().trim() !== '');
    return Math.round((filledFields.length / fields.length) * 100);
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'error';
  };

  return (
    <AppBar 
      position="sticky" 
      elevation={1}
      sx={{
        background: 'linear-gradient(135deg, #29204C 0%, #1a1530 100%)',
        backdropFilter: 'blur(10px)',
        borderBottom: '3px solid #4caf50',
        minHeight: { xs: 70, sm: 80 },
      }}
    >
      <Toolbar sx={{ 
        justifyContent: 'space-between', 
        px: { xs: 2, sm: 3, md: 4 },
        minHeight: { xs: 70, sm: 80 },
        position: 'relative'
      }}>
        {/* Left Section - Logo & Date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img 
              src="/zonnetLogo.png" 
              alt="ZonNet Logo"
              style={{
                height: '50px',
                width: 'auto',
                filter: 'brightness(0) invert(1)', // Make logo white
              }}
              onError={(e) => {
                // Fallback to text if logo not found
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                display: 'none',
                fontWeight: 600,
                color: 'white',
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              ZonNet
            </Typography>
          </Box>
          
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.875rem',
                fontWeight: 500,
                lineHeight: 1.2
              }}
            >
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.2,
                mt: 0.5,
                letterSpacing: '0.5px'
              }}
            >
              {currentTime.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
              })}
            </Typography>
          </Box>
        </Box>

        {/* Center Section - Title */}
        <Box sx={{ 
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          textAlign: 'center'
        }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600,
              color: 'white',
              fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.4rem' }
            }}
          >
            Candidate Portal
          </Typography>
        </Box>

        {/* Right Section - Profile & Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Notification Bell */}
          <NotificationBell candidateId={candidateData?.candidate_id} />

          {/* Profile Menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              onClick={handleMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '12px',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  border: '2px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                {candidateData ? getInitials(candidateData.full_name) : 'C'}
              </Avatar>
              
              <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left' }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '1rem',
                    lineHeight: 1.2,
                  }}
                >
                  {candidateData?.full_name || 'Candidate'}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '0.875rem',
                    lineHeight: 1,
                  }}
                >
                  {candidateData?.candidate_id || 'ID: N/A'}
                </Typography>
              </Box>
              
              <ArrowDownIcon 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.8)', 
                  fontSize: '20px',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </Box>

            {/* Profile Dropdown Menu */}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              PaperProps={{
                elevation: 8,
                sx: {
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 1.5,
                  minWidth: 220,
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              {/* Profile Header */}
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#29204C' }}>
                  {candidateData?.full_name || 'Candidate Name'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {candidateData?.email || 'No email provided'}
                </Typography>
              </Box>

              {/* Menu Items */}
              <MenuItem onClick={handleProfile} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" sx={{ color: '#29204C' }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    My Profile
                  </Typography>
                </ListItemText>
              </MenuItem>

              <MenuItem onClick={handleSettings} sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" sx={{ color: '#666' }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Account Settings
                  </Typography>
                </ListItemText>
              </MenuItem>

              <Divider sx={{ my: 1 }} />

              <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: '#d32f2f' }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" sx={{ color: '#d32f2f' }} />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#d32f2f' }}>
                    Sign Out
                  </Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default CandidateHeader;